import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../../components/Shell";
import { useCart } from "../../context/CartContext";
import api, { formatApiError } from "../../lib/api";
import AuthImage from "../../components/AuthImage";
import { Trash2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
    const { items, setQty, remove, clear, totalPrice } = useCart();
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const submit = async () => {
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
                clinic_notes: notes,
            };
            const { data } = await api.post("/orders", payload);
            clear();
            toast.success(`Order ${data.order_no} submitted`);
            navigate(`/orders?confirmed=${data.order_no}`);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (items.length === 0) {
        return (
            <Shell variant="clinic">
                <div className="max-w-3xl mx-auto px-6 py-20 text-center animate-fade-in">
                    <p className="eyebrow mb-3">Cart</p>
                    <h1 className="font-heading text-3xl font-light mb-4">Your cart is empty</h1>
                    <Link to="/catalogue" data-testid="cart-browse" className="btn-primary">Browse catalogue</Link>
                </div>
            </Shell>
        );
    }

    return (
        <Shell variant="clinic">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <p className="eyebrow mb-2">Order request</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-2">Review your cart</h1>
                <p className="text-sm text-zinc-500 mb-10">No payment is taken on this portal. Your order request is sent to Dermalis for manual processing.</p>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    <div className="lg:col-span-2 card-base divide-y divide-zinc-100" data-testid="cart-items">
                        {items.map(i => (
                            <div key={i.product_id} className="p-4 flex items-center gap-4">
                                <div className="w-16 h-16 bg-zinc-50 flex-shrink-0">
                                    <AuthImage path={i.image_path} alt={i.name} className="w-full h-full object-contain p-2" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{i.name}</p>
                                    <p className="text-xs text-zinc-500">SKU {i.sku} · ${(i.price || 0).toFixed(2)}</p>
                                </div>
                                <div className="inline-flex items-center border border-zinc-200 rounded-sm">
                                    <button data-testid={`cart-minus-${i.product_id}`} onClick={() => setQty(i.product_id, i.quantity - 1)} className="px-2 py-1.5 hover:bg-zinc-50"><Minus size={12} strokeWidth={1.5} /></button>
                                    <span className="w-9 text-center text-sm">{i.quantity}</span>
                                    <button data-testid={`cart-plus-${i.product_id}`} onClick={() => setQty(i.product_id, i.quantity + 1)} className="px-2 py-1.5 hover:bg-zinc-50"><Plus size={12} strokeWidth={1.5} /></button>
                                </div>
                                <div className="w-24 text-right text-sm font-medium">${((i.price || 0) * i.quantity).toFixed(2)}</div>
                                <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-zinc-400 hover:text-red-700"><Trash2 size={16} strokeWidth={1.5} /></button>
                            </div>
                        ))}
                    </div>

                    <aside className="card-base p-6 h-fit">
                        <p className="eyebrow mb-4">Summary</p>
                        <div className="flex items-baseline justify-between mb-6">
                            <span className="text-sm text-zinc-500">Estimated total</span>
                            <span data-testid="cart-total" className="font-heading text-2xl">${totalPrice.toFixed(2)} <span className="text-xs text-zinc-400">NZD</span></span>
                        </div>
                        <label className="input-label">Notes for Dermalis</label>
                        <textarea data-testid="cart-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Delivery preferences, urgency, anything else…" className="input-base min-h-[100px]" />
                        {error && <div className="text-sm text-red-700 mt-3">{error}</div>}
                        <button data-testid="cart-submit" onClick={submit} disabled={submitting} className="btn-primary w-full mt-5">{submitting ? "Submitting…" : "Submit order request"}</button>
                        <p className="text-[11px] text-zinc-400 mt-3 leading-relaxed">By submitting this order you authorise Dermalis NZ to contact you to confirm details. No payment is processed online.</p>
                    </aside>
                </div>
            </div>
        </Shell>
    );
}
